import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ITask } from '@nimbus-assistant/models/task.model';
import { FooterPriority, getFooterPriority, isTransferDisabled } from '@nimbus-assistant/helpers/task-priority.helpers';
import { AssistantTasksService } from './assistant-tasks.service';
import { UserStateService } from './user-state.service';

export enum TaskFooterType {
  Transfer = 'transfer',
  OnHold = 'on-hold',
  ConnectedInfo = 'connected-info',
  IncomingInfo = 'incoming-info',
  Parked = 'parked',
  NarRequest = 'nar-request',
}

export interface ITaskFooterState {
  type: TaskFooterType;
  connectedTask?: ITask;
  incomingTask?: ITask;
  parkedCount?: number;
  isTransferDisabled?: boolean;
  narMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class TaskFooterService {
  public readonly footerState$: Observable<ITaskFooterState | null> = combineLatest([
    this.tasksService.connectedTask$,
    this.tasksService.incomingTask$,
    this.tasksService.parkedTasks$,
    this.tasksService.isAudioCall$,
    this.userStateService.hasOpenNarRequest$,
    this.userStateService.narMessage$,
  ]).pipe(
    map(([connected, incoming, parked, isAudio, hasOpenNarRequest, narMessage]) =>
      this.computeFooterState(connected, incoming, parked, isAudio, hasOpenNarRequest, narMessage),
    ),
    distinctUntilChanged(
      (a, b) =>
        a?.type === b?.type &&
        a?.connectedTask?.unifiedConversationId === b?.connectedTask?.unifiedConversationId &&
        a?.connectedTask?.consultationSession?.currentState === b?.connectedTask?.consultationSession?.currentState &&
        a?.connectedTask?.consultationSession?.targetState?.state === b?.connectedTask?.consultationSession?.targetState?.state &&
        a?.connectedTask?.callerHungUpAt === b?.connectedTask?.callerHungUpAt &&
        a?.connectedTask?.conversationState === b?.connectedTask?.conversationState &&
        a?.incomingTask?.unifiedConversationId === b?.incomingTask?.unifiedConversationId &&
        a?.parkedCount === b?.parkedCount &&
        a?.isTransferDisabled === b?.isTransferDisabled &&
        a?.narMessage === b?.narMessage,
    ),
  );

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly userStateService: UserStateService,
  ) {}

  private computeFooterState(
    connected: ITask | null,
    incoming: ITask | null,
    parked: ITask[],
    isAudio: boolean,
    hasOpenNarRequest: boolean,
    narMessage: string,
  ): ITaskFooterState | null {
    const priority = getFooterPriority(connected, incoming, parked?.length ?? 0, isAudio, hasOpenNarRequest);

    switch (priority) {
      case FooterPriority.Transfer:
        return {
          type: TaskFooterType.Transfer,
          connectedTask: connected,
          isTransferDisabled: isTransferDisabled(connected),
        };
      case FooterPriority.OnHold:
        return {
          type: TaskFooterType.OnHold,
          connectedTask: connected,
        };
      case FooterPriority.ConnectedInfo:
        return {
          type: TaskFooterType.ConnectedInfo,
          connectedTask: connected,
        };
      case FooterPriority.IncomingInfo:
        return {
          type: TaskFooterType.IncomingInfo,
          incomingTask: incoming,
        };
      case FooterPriority.Parked:
        return {
          type: TaskFooterType.Parked,
          parkedCount: parked.length,
        };
      case FooterPriority.NarRequest:
        return {
          type: TaskFooterType.NarRequest,
          narMessage,
        };
      default:
        return null;
    }
  }
}
